import { smoothMaskAlpha } from './edge-mask.js';
const W=1672,H=941;
function surface(x,y,w,h){const c=document.createElement('canvas');c.width=w;c.height=h;c.originX=x;c.originY=y;return c;}
const clamp=x=>Math.max(0,Math.min(1,x));
const smooth=x=>{x=clamp(x);return x*x*(3-2*x);};

// Lighting is evaluated once in reference coordinates, not read back during animation.
export function createLighting(){
 const maps=[false,true].map(lantern=>{
  const c=surface(0,0,W,H),ctx=c.getContext('2d'),image=ctx.createImageData(W,H);
  for(let y=0;y<H;y++)for(let x=0;x<W;x++){
   const ground=smooth((y-475)/180);
   const distance=Math.hypot((x-1040)/525,(y-755)/340);
   const characterBounce=smooth(1-Math.hypot((x-790)/255,(y-545)/300))*.28;
   const fire=Math.max(smooth(1-distance)*ground*.94,characterBounce);
   const lamp=lantern?smooth(1-Math.hypot((x-1555)/170,(y-359)/205))*.74:0;
   const treasure=smooth(1-Math.hypot((x-1570)/195,(y-635)/185))*.55;
   const i=(y*W+x)*4;
   for(let k=0;k<3;k++){
    const ambient=[22,31,52][k],warm=[255,190,116][k],gold=[255,211,135][k];
    image.data[i+k]=Math.max(ambient+(warm-ambient)*fire,ambient+(gold-ambient)*lamp,ambient+(gold-ambient)*treasure);
   }
   image.data[i+3]=255;
  }
  ctx.putImageData(image,0,0);return c;
 });
 const ember=surface(875,570,350,320),x=ember.getContext('2d');
 const halo=x.createRadialGradient(170,175,8,170,175,170);
 halo.addColorStop(0,'rgba(255,178,68,.18)');halo.addColorStop(.45,'rgba(255,117,25,.055)');halo.addColorStop(1,'rgba(255,110,20,0)');
 x.fillStyle=halo;x.fillRect(0,0,350,320);
 return {maps,ember};
}

export function createChestGlow(mask){
 const pad=80,c=surface(mask.originX-pad,mask.originY-pad,mask.width+pad*2,mask.height+pad*2);
 const edge=surface(0,0,c.width,c.height),e=edge.getContext('2d');
 for(let i=0;i<16;i++)e.drawImage(mask,pad+Math.cos(i*Math.PI/8)*9,pad+Math.sin(i*Math.PI/8)*9);
 e.globalCompositeOperation='source-in';e.fillStyle='#ffc454';e.fillRect(0,0,c.width,c.height);
 e.globalCompositeOperation='destination-out';e.drawImage(mask,pad,pad);
 e.globalCompositeOperation='destination-in';
 const fade=e.createLinearGradient(0,675-c.originY,0,755-c.originY);fade.addColorStop(0,'white');fade.addColorStop(1,'transparent');
 e.fillStyle=fade;e.fillRect(0,0,c.width,c.height);
 const x=c.getContext('2d');
 // Blur the source itself: shadowBlur alone also redraws an unwanted sharp contour.
 for(const [blur,alpha]of [[26,1],[12,.95],[5,.65]]){x.save();x.globalAlpha=alpha;x.filter=`blur(${blur}px)`;x.drawImage(edge,0,0);x.restore();}
 const seamLight=surface(0,0,c.width,c.height),s=seamLight.getContext('2d');
 s.translate(-c.originX,-c.originY);
 // The latch occludes the lid joint; do not paint light across the lock.
 const seam=new Path2D('M1451 612 L1524 622 M1560 627 L1637 638 L1672 626');
 s.lineCap='round';s.lineJoin='round';s.lineWidth=12;s.strokeStyle='#ffe9a0';s.stroke(seam);
 for(const [blur,alpha]of [[22,1],[9,1],[3,.8]]){x.save();x.globalAlpha=alpha;x.filter=`blur(${blur}px)`;x.drawImage(seamLight,0,0);x.restore();}
 return c;
}

export function createTentSilhouette(source,chest){
 const left=1170,top=150,w=502,h=505;
 const c=surface(left,top,w,h),x=c.getContext('2d');
 x.translate(-left,-top);x.fillStyle='white';
 x.fill(new Path2D('M1500 175 L1672 179 L1672 572 L1486 551 Q1452 573 1445 645 L1376 633 L1250 610 L1300 427 Z'));
 x.setTransform(1,0,0,1,0,0);
 const shape=x.getImageData(0,0,w,h),pixels=source.getContext('2d').getImageData(left,top,w,h).data;
 const occlusion=surface(0,0,w,h),o=occlusion.getContext('2d'),occluded=o.createImageData(w,h);
 // Snap the exposed fabric edge to a sustained run of red canvas, ignoring thin guy ropes.
 for(let row=30;row<440;row++){
  let run=0,boundary=-1;
  for(let col=0;col<w;col++){
   const i=(row*w+col)*4,r=pixels[i],g=pixels[i+1],b=pixels[i+2];
   run=r>50&&r>g*1.3&&r>b*1.3?run+1:0;
   if(run===14){boundary=Math.max(0,col-15);break;}
  }
  if(boundary>=0)for(let col=0;col<boundary;col++)shape.data[(row*w+col)*4+3]=0;
 }
 for(let i=0;i<pixels.length;i+=4){
  const r=pixels[i],g=pixels[i+1],b=pixels[i+2];
  const y=top+Math.floor(i/4/w),px=left+(i/4)%w;
  const foliage=g>r*.98&&g>b*1.04&&(y>570||(px>1600&&y>475));
  if(foliage){shape.data[i+3]=0;occluded.data[i+3]=255;}
 }
 x.putImageData(smoothMaskAlpha(shape),0,0);o.putImageData(occluded,0,0);
 x.globalCompositeOperation='destination-out';x.drawImage(chest,chest.originX-left,chest.originY-top);x.globalCompositeOperation='source-over';
 const outline=surface(left,top,w,h),edge=outline.getContext('2d');
 for(let a=0;a<8;a++)edge.drawImage(c,Math.cos(a*Math.PI/4)*1.2,Math.sin(a*Math.PI/4)*1.2);
 edge.globalCompositeOperation='destination-out';edge.drawImage(c,0,0);
 // Occluding leaves/chest must not acquire the tent's outline.
 for(let a=0;a<8;a++){
  const dx=Math.cos(a*Math.PI/4)*4,dy=Math.sin(a*Math.PI/4)*4;
  edge.drawImage(occlusion,dx,dy);edge.drawImage(chest,chest.originX-left+dx,chest.originY-top+dy);
 }
 edge.beginPath();edge.arc(1500-left,170-top,15,0,Math.PI*2);edge.fill();
 edge.globalCompositeOperation='source-in';edge.fillStyle='#fffef4';edge.fillRect(0,0,w,h);
 const soft=surface(left,top,w,h),sx=soft.getContext('2d');sx.filter='blur(.45px)';sx.drawImage(outline,0,0);
 const alpha=x.getImageData(0,0,w,h).data;
 return {outline:soft,hit(px,py){px=Math.floor(px-left);py=Math.floor(py-top);return px>=0&&py>=0&&px<w&&py<h&&alpha[(py*w+px)*4+3]>80;}};
}
